import { useMemo } from 'react';
import {
  splitPotCents,
  validatePayoutStructure,
  formatDollars,
  type PayoutSlot,
  MIN_PLACES,
  MAX_PLACES,
} from '@/lib/payouts';
import { cn } from '@/lib/utils';

type Props = {
  potCents: number;
  slots: PayoutSlot[];
  onChange: (next: PayoutSlot[]) => void;
};

/**
 * Configurable paying-places editor. Lets the admin set N places (1..MAX),
 * pick a percent for each, and shows live $ amounts using the same Hare
 * (largest-remainder) splitter as the server.
 *
 * Surfaces the exact sum + delta so the admin sees what's off if !=100.
 */
export function PayoutEditor({ potCents, slots, onChange }: Props) {
  const v = useMemo(() => validatePayoutStructure(slots), [slots]);
  const sum = slots.reduce((s, p) => s + p.pct, 0);
  const sumOk = Math.abs(sum - 100) < 1e-6;

  const amounts = useMemo<number[]>(() => {
    if (!v.ok) return slots.map(() => 0);
    try {
      return splitPotCents(potCents, slots);
    } catch {
      return slots.map(() => 0);
    }
  }, [potCents, slots, v.ok]);

  const setPct = (i: number, pct: number) => {
    const next = slots.slice();
    next[i] = { ...next[i]!, pct };
    onChange(next);
  };

  const addPlace = () => {
    if (slots.length >= MAX_PLACES) return;
    onChange([...slots, { place: slots.length + 1, pct: 0 }]);
  };

  const removePlace = () => {
    if (slots.length <= MIN_PLACES) return;
    const next = slots.slice(0, -1);
    onChange(next);
  };

  const evenSplit = () => {
    const n = slots.length;
    const each = Math.round((100 / n) * 100) / 100;
    const drift = Math.round((100 - each * n) * 100) / 100;
    const next = slots.map((s, i) => ({
      ...s,
      pct: i === 0 ? Math.round((each + drift) * 100) / 100 : each,
    }));
    onChange(next);
  };

  const sumDelta = Math.round((sum - 100) * 100) / 100;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={removePlace}
            disabled={slots.length <= MIN_PLACES}
            className="h-9 w-9 rounded-md bg-cabinet-cream/5 ring-1 ring-cabinet-cream/20 text-cabinet-cream hover:bg-cabinet-cream/10 disabled:opacity-30"
            aria-label="Remove last place"
          >
            −
          </button>
          <p className="font-display text-cabinet-gold tracking-widest uppercase text-sm w-24 text-center">
            {slots.length} {slots.length === 1 ? 'place' : 'places'}
          </p>
          <button
            type="button"
            onClick={addPlace}
            disabled={slots.length >= MAX_PLACES}
            className="h-9 w-9 rounded-md bg-cabinet-gold text-cabinet-purple-ink font-bold hover:brightness-105 disabled:opacity-30"
            aria-label="Add a place"
          >
            +
          </button>
        </div>
        <button
          type="button"
          onClick={evenSplit}
          className="text-xs text-cabinet-cream/60 hover:text-cabinet-gold underline-offset-2 hover:underline"
        >
          Even split
        </button>
      </div>

      <ul className="space-y-2">
        {slots.map((s, i) => (
          <li
            key={i}
            className="flex items-center gap-3 rounded-md bg-cabinet-purple-ink/60 ring-1 ring-cabinet-cream/10 px-3 py-2"
          >
            <span className="font-display text-cabinet-gold text-lg w-10 shrink-0">
              {placeLabel(s.place)}
            </span>
            <div className="relative flex-1">
              <input
                type="number"
                step="0.01"
                min={0}
                max={100}
                value={Number.isFinite(s.pct) ? s.pct : 0}
                onChange={(e) => setPct(i, parseFloat(e.target.value) || 0)}
                className="w-full bg-black/50 border-2 border-cabinet-cream/20 rounded-md px-3 py-2 pr-9 text-cabinet-cream focus:border-cabinet-gold focus:outline-none focus:ring-2 focus:ring-cabinet-gold/40 transition tabular-nums"
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-cabinet-cream/40 text-sm">
                %
              </span>
            </div>
            <span
              className={cn(
                'w-24 text-right font-led tabular-nums tracking-wider',
                sumOk && potCents > 0
                  ? 'text-cabinet-led-on'
                  : 'text-cabinet-cream/40',
              )}
            >
              {formatDollars(amounts[i] ?? 0)}
            </span>
          </li>
        ))}
      </ul>

      <div className="flex items-center justify-between text-xs uppercase tracking-widest">
        <span className="text-cabinet-cream/60">Total</span>
        <span
          className={cn(
            'font-display text-base',
            sumOk
              ? 'text-cabinet-gold-bright'
              : 'text-cabinet-flame-orange',
          )}
        >
          {sum.toFixed(2)}%
          {!sumOk && (
            <span className="ml-2 text-cabinet-flame-orange">
              ({sumDelta > 0 ? `+${sumDelta}` : sumDelta} off)
            </span>
          )}
        </span>
      </div>

      {!v.ok && (
        <p className="text-xs text-cabinet-flame-orange">{v.error}</p>
      )}
    </div>
  );
}

function placeLabel(p: number): string {
  const v = p % 100;
  if (v >= 11 && v <= 13) return `${p}th`;
  switch (p % 10) {
    case 1:
      return `${p}st`;
    case 2:
      return `${p}nd`;
    case 3:
      return `${p}rd`;
    default:
      return `${p}th`;
  }
}
